import AddIcon from "@mui/icons-material/Add";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { styled, Theme } from "@mui/material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { MRTNoMemo } from "_shared/_views/tables/table";
import Guid from "_shared/guids/guid";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  MRT_ToggleGlobalFilterButton as ShowHideColumnsButton,
  type MRT_SortingState as SortingState,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo } from "react";
import { useLoaderData, useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import UserInformation from "users/information/user-information";
import { PageTab } from "users/networking/_views/network-page-tabs";
import NetworkInvitationInfo from "users/networking/connections/invitations/network-invitation-info";
import CreateNetworkedGroupApiRequest from "users/networking/groups/_api/commands/create-networked-group-api-command";
import UpdateNetworkedGroupApiRequest from "users/networking/groups/_api/commands/update-networked-group-api-command";
import { useCreateNetworkedGroupMutation, useQueryNetworkedGroupsQuery, useUpdateNetworkedGroupMutation } from "users/networking/groups/_api/networked-group-api";
import NetworkedGroupQuery, {
  NetworkedGroupFilterParameters,
  NetworkedGroupOrderField,
  NetworkedGroupOrderParameters,
} from "users/networking/groups/_api/responses/networked-groups-api-query";
import CreateNetworkedGroupComponent from "users/networking/groups/_views/create-networked-group";
import EditNetworkedGroupComponent from "users/networking/groups/_views/edit-networked-group";
import NetworkedGroupAvatarGroup from "users/networking/groups/_views/networked-group-avatar-group";
import NetworkedGroup from "users/networking/groups/networked-group";

const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));
const TableTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const NewButton = styled(IconButton)(() => ({
  padding: 0,
}));

const columnHelper = createMRTColumnHelper<NetworkedGroup>();

export default function GroupsTab() {
  const handledRef = React.useRef<string | null>(null);

  const [globalFilter, setGlobalFilter] = React.useState("");
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  const navigate = useNavigate();
  const session = useSession();
  const { openDialog } = useDialog();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  // Route
  const routeParams = useLoaderData();
  const routeAction = routeParams?.action as string | undefined;
  const routeId = routeParams?.id as string | undefined;

  const name = columnFilters.find((filter) => filter.id === "name")?.value as string;
  const description = columnFilters.find((filter) => filter.id === "description")?.value as string;

  // #region Queries
  const query = useMemo(() => {
    const paginationParameters = PaginationParameters.create(pagination);

    const filterParameters = new NetworkedGroupFilterParameters();
    filterParameters.name = name;
    filterParameters.description = description;

    const orderParameters = new NetworkedGroupOrderParameters(
      (sorting[0]?.id as NetworkedGroupOrderField) ?? NetworkedGroupOrderField.Name,
      sorting[0]?.desc === false ? OrderDirection.Ascending : OrderDirection.Descending
    );

    return new NetworkedGroupQuery(
      filterParameters,
      orderParameters,
      paginationParameters
    );
  }, [pagination, name, description, sorting]);

  const networkedGroupQuery = useQueryNetworkedGroupsQuery(query);
  const [createNetworkedGroupQuery, createGroupResult] = useCreateNetworkedGroupMutation();
  const [updateNetworkedGroupQuery, updateGroupResult] = useUpdateNetworkedGroupMutation();

  const networkedGroups = useMemo(() => {
    if (!networkedGroupQuery.data) {
      return PaginatedResponse.empty<NetworkedGroup>();
    }
    return PaginatedResponse.from<NetworkedGroup>(
      NetworkedGroup.fromJSONArray(networkedGroupQuery.data?.data ?? []),
      networkedGroupQuery.data?.headers
    );
  }, [networkedGroupQuery.data]);

  const createNetworkedGroup = useCallback(async (payload: CreateNetworkedGroupApiRequest | null) => {
    if (!payload) {
      navigate(`/network/${PageTab.Groups}`);
      return;
    }

    await createNetworkedGroupQuery(payload);

    if (createGroupResult.isSuccess) {
      navigate(`/network/${PageTab.Groups}/edit/${createGroupResult.data.id}`);
      enqueueSnackbar("Networked group created", {
        variant: "success"
      });
    } else {
      console.error(createGroupResult.error);
      enqueueSnackbar("Failed to create networked group", {
        variant: "error"
      });
    }
  }, [createNetworkedGroupQuery, createGroupResult, navigate]);

  const openCreateGroupDialog = useCallback(() => {
    openDialog(
      {
        title: "Create Networked Group",
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        MuiProps: {
          maxWidth: "md",
          fullWidth: true,
        },
        onClose: () => {
          navigate(`/network/${PageTab.Groups}`);
        },
        component: (
          <CreateNetworkedGroupComponent
            onCreate={createNetworkedGroup}
          />
        ),
      },
    );
  }, [openDialog, createNetworkedGroup, navigate]);


  const updateNetworkedGroup = useCallback(async (
    groupId: Guid,
    payload: UpdateNetworkedGroupApiRequest | undefined
  ) => {
    if (!payload) {
      navigate(`/network/${PageTab.Groups}`);
      return;
    }

    await updateNetworkedGroupQuery({ id: groupId, data: payload });

    if (updateGroupResult.isSuccess) {
      navigate(`/network/${PageTab.Groups}`);
      enqueueSnackbar("Networked group updated", {
        variant: "success",
      });
    } else {
      console.error(updateGroupResult.error);
      enqueueSnackbar("Failed to update networked group", {
        variant: "error",
      });
    }
  }, [updateNetworkedGroupQuery, updateGroupResult, navigate]);

  const openEditGroupDialog = useCallback((groupId: Guid) => {
    openDialog(
      {
        title: "Update Networked Group",
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        MuiProps: {
          maxWidth: "md",
          fullWidth: true,
        },
        onClose: () => {
          navigate(`/network/${PageTab.Groups}`)
        },
        component: (
          <EditNetworkedGroupComponent
            groupId={groupId}
            onSave={(payload) => updateNetworkedGroup(groupId, payload)}
          />
        ),
      },
    );
  }, [openDialog, updateNetworkedGroup, navigate]);

  const handleViewProfile = useCallback((individualId?: Guid, data?: NetworkedGroup): void => {
    if (!individualId) return;

    let individualProfile: IndividualProfile | undefined;
    if (data instanceof UserInformation) {
      individualProfile = new IndividualProfile({
        userId: data.id,
        firstName: data.name.firstName ?? "",
        lastName: data.name.lastName ?? "",
        avatarId: data.avatarId,
        legalEntityId: data.legalEntityId,
        email: data.email,
      });
    } else if (data instanceof NetworkInvitationInfo) {
      individualProfile = new IndividualProfile({
        userId: data.user.id,
        firstName: data.user.name.firstName ?? "",
        lastName: data.user.name.lastName ?? "",
        avatarId: data.avatarId,
        email: data.user.email ?? undefined,
      });
    }

    openDialog({
      titleStyle: {
        paddingBottom: 0
      },
      contentSxProps: {
        display: "flex",
        overflowX: "hidden",
      },
      MuiProps: {
        maxWidth: "lg",
        fullWidth: false,
      },
      component: (
        <ViewIndividualProfile
          individualId={individualId}
          profile={individualProfile}
        />
      ),
    });
  }, [openDialog]);

  // Handle URL changes
  useEffect(() => {
    const key = `${routeAction}:${routeId}`;
    if (handledRef.current === key) return;
    
    if (routeAction === "new") {
      handledRef.current = key;
      openCreateGroupDialog();
      return;
    } 

    if (!routeId) return;

    if (routeAction === "edit") {
      handledRef.current = key;
      openEditGroupDialog(new Guid(routeId));
      return;
    } 

    if (routeAction === "view") {
      const match = networkedGroups.data.find(group => group.id?.value === routeId);
      if (!match) return;

      handledRef.current = key;
      handleViewProfile(new Guid(routeId), match);
    }
  }, [handleViewProfile, networkedGroups.data, openCreateGroupDialog, openEditGroupDialog, routeAction, routeId]);

  const renderNameCell = useCallback((group: NetworkedGroup) => {
    return (
      <Typography variant="body1" fontWeight="bold">
        {group.name}
      </Typography>
    );
  }, []);

  const renderMembersCell = useCallback((group: NetworkedGroup) => {
    return <NetworkedGroupAvatarGroup
      group={group}
      session={session}
      avatarGroupMax={3}
    />
  }, [session]);

  const columns = useMemo(() => [
    columnHelper.display({
      header: "Name",
      id: "name",
      Cell: ({ row }) => renderNameCell(row.original),
    }),
    columnHelper.display({
      header: "Description",
      id: "description",
      Cell: ({ row }) => {
        return row.original.description;
      }
    }),
    columnHelper.display({
      header: "Members",
      id: "members",
      Cell: ({ row }) => renderMembersCell(row.original)
    }),
  ], [renderMembersCell, renderNameCell]);

  const tableOptions: MRT_TableOptions<NetworkedGroup> = {
    columns: columns,
    data: networkedGroups.data,
    getRowId: (row) => row.id?.value ?? "",
    enableRowSelection: false,
    enableTableHead: true,
    enableColumnPinning: true,
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <ToggleGlobalFilterButton table={table} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleDensePaddingButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
            <Button startIcon={<AddIcon />} onClick={() => navigate(`/network/${PageTab.Groups}/new`)}>
              New Group
            </Button>
          </ToolbarButtonsContainer>
        )}
        {isMediumDisplaySize && (
          <NewButton color="primary" onClick={() => navigate("/marketplace")}>
            <AddBoxIcon fontSize="large" />
          </NewButton>
        )}
      </>
    ),
    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <TableTopToolbarContainer>
          <TableTitle variant="h4">Network</TableTitle>
          <LowerTopToolbarContainer>
            <Tabs
              value={PageTab.Groups}
              indicatorColor="primary"
              textColor="primary"
              onChange={(_event, newValue) => {
                navigate(`/network/${newValue}`);
              }}
            >
              <Tab value={PageTab.Contacts} label="Contacts" />
              <Tab value={PageTab.Coworkers} label="Coworkers" />
              <Tab value={PageTab.Pending} label="Pending" />
              <Tab value={PageTab.Groups} label="Groups" />
            </Tabs>
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <ToolbarButtonsContainer>
              <ToolbarButtons>
                <ToggleGlobalFilterButton table={table} />
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleDensePaddingButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </ToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        navigate(`/network/groups/edit/${row.original.id?.value}`);
      },
      sx: { cursor: "pointer" },
    }),
    muiTableContainerProps: () => ({
      sx: {
        flex: 1
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column"
      },
    },
    muiToolbarAlertBannerProps: networkedGroupQuery.isError
      ? {
        color: "error",
        children: "Error loading data",
      }
      : undefined,
    rowCount: networkedGroups.totalElements ?? 0,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    state: {
      globalFilter,
      columnFilters,
      // isLoading: networkedGroupQuery.isLoading,
      isLoading: false,
      pagination,
      showAlertBanner: networkedGroupQuery.isError,
      // showProgressBars: networkedGroupQuery.isFetching,
      showProgressBars: false,
      // showSkeletons: networkedGroupQuery.isLoading,
      showSkeletons: false,
      sorting,
    },
  }

  return <MRTNoMemo<NetworkedGroup> options={tableOptions} />;
}
